import { brand as defaultBrand, cloneBrandConfig } from "../config/brand.js";

const SETTINGS_SECTIONS = [
  {
    id: "identity",
    label: "Identity",
    eyebrow: "Brand voice",
    description:
      "Update the copy that appears in the hero and loading state.",
    fields: [
      ["identity.name", "Brand name", "text"],
      ["identity.shortName", "Short mark", "text"],
      ["identity.eyebrow", "Eyebrow", "text"],
      ["identity.headline", "Headline", "textarea"],
      ["identity.subheadline", "Subheadline", "textarea"],
      ["identity.status", "Status pill", "text"],
      ["identity.loadingMessage", "Loading message", "text"],
      ["identity.footerNote", "Closing note", "textarea"],
    ],
  },
  {
    id: "layout",
    label: "Layout",
    eyebrow: "Homepage structure",
    description:
      "Layer 1 is the scrolling hero intro; layer 2 is the full-width story stack below it.",
  },
  {
    id: "theme",
    label: "Theme",
    eyebrow: "Visual system",
    description:
      "Tune the core palette used by CSS variables and Three.js materials.",
    fields: [
      ["theme.colors.background", "Background", "color"],
      ["theme.colors.backgroundAlt", "Background alt", "color"],
      ["theme.colors.primary", "Primary", "color"],
      ["theme.colors.secondary", "Secondary", "color"],
      ["theme.colors.accent", "Accent", "color"],
      ["theme.colors.text", "Text", "color"],
      ["theme.colors.muted", "Muted text", "color"],
      ["theme.colors.grid", "Grid", "color"],
    ],
  },
  {
    id: "scene",
    label: "Scene",
    eyebrow: "3D mood",
    description: "Adjust material, fog, particle, and scroll motion values.",
    fields: [
      [
        "scene.heroObject.metalness",
        "Metalness",
        "range",
        { min: 0, max: 1, step: 0.01 },
      ],
      [
        "scene.heroObject.roughness",
        "Roughness",
        "range",
        { min: 0, max: 1, step: 0.01 },
      ],
      [
        "scene.particles.size",
        "Particle size",
        "range",
        { min: 0.01, max: 0.12, step: 0.005 },
      ],
      [
        "scene.particles.opacity",
        "Particle opacity",
        "range",
        { min: 0.1, max: 1, step: 0.01 },
      ],
      [
        "scene.particles.floatAmplitude",
        "Particle float",
        "range",
        { min: 0, max: 1.4, step: 0.05 },
      ],
      [
        "scene.fog.far",
        "Fog distance",
        "number",
        { min: 12, max: 120, step: 1 },
      ],
      [
        "animation.scrollCameraDepth",
        "Scroll camera depth",
        "range",
        { min: 0, max: 4, step: 0.1 },
      ],
      [
        "animation.scrollRotation",
        "Scroll rotation",
        "range",
        { min: 0, max: 5, step: 0.1 },
      ],
    ],
  },
];

export class SettingsPage {
  constructor({
    getBrandConfig,
    onPreview,
    onSave,
    onReset,
    defaultConfig = defaultBrand,
  }) {
    this.getBrandConfig = getBrandConfig;
    this.onPreview = onPreview;
    this.onSave = onSave;
    this.onReset = onReset;
    this.defaultConfig = defaultConfig;
    this.activeSection = "identity";
    this.draft = cloneBrandConfig(getBrandConfig());
    this.original = cloneBrandConfig(getBrandConfig());
    this.root = null;
    this.trigger = null;
    this.formContent = null;
    this.livePreviewInput = null;
  }

  mount() {
    this.trigger = document.createElement("a");
    this.trigger.className = "settings-floating-button";
    this.trigger.href = "#settings";
    this.trigger.setAttribute("aria-label", "Open site settings");
    this.trigger.innerHTML = `
      <span aria-hidden="true"></span>
      <strong>Settings</strong>
    `;
    document.body.appendChild(this.trigger);

    this.root = document.createElement("section");
    this.root.className = "settings-page";
    this.root.hidden = true;
    this.root.innerHTML = this.getShellMarkup();
    document.body.appendChild(this.root);

    this.formContent = this.root.querySelector("[data-settings-content]");
    this.livePreviewInput = this.root.querySelector(
      "[data-settings-live-preview]",
    );
    this.bindEvents();
    this.renderSection();
    this.syncHashState();
  }

  getShellMarkup() {
    return `
      <div class="settings-backdrop" data-settings-close></div>
      <aside
        class="settings-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
      >
        <header class="settings-header">
          <div>
            <p class="settings-kicker">AI & Computer Services</p>
            <h2 id="settings-title">Settings</h2>
            <p>Preview and save your landing copy and colors. Saved values persist in this browser until you reset.</p>
          </div>
          <button class="settings-icon-button" type="button" data-settings-close aria-label="Close settings">
            <span aria-hidden="true"></span>
          </button>
        </header>

        <div class="settings-toolbar">
          <label class="settings-live-toggle">
            <input type="checkbox" data-settings-live-preview checked />
            <span>Live preview</span>
          </label>
          <a class="settings-page-link" href="#story-start">Back to hero</a>
        </div>

        <nav class="settings-tabs" aria-label="Settings sections">
          ${SETTINGS_SECTIONS.filter((section) => section.id !== "layout").map(
            (section) => `
              <button
                type="button"
                data-settings-tab="${section.id}"
                aria-selected="${section.id === this.activeSection}"
              >
                ${section.label}
              </button>
            `,
          ).join("")}
          <button
            type="button"
            data-settings-tab="layout"
            aria-selected="false"
          >
            Layout
          </button>
          <button
            type="button"
            data-settings-tab="story"
            aria-selected="false"
          >
            Story
          </button>
          <button
            type="button"
            data-settings-tab="cards"
            aria-selected="false"
          >
            Cards
          </button>
        </nav>

        <div class="settings-content" data-settings-content></div>

        <footer class="settings-footer">
          <button class="settings-button settings-button-ghost" type="button" data-settings-reset>
            Reset defaults
          </button>
          <div>
            <button class="settings-button settings-button-secondary" type="button" data-settings-close>
              Cancel
            </button>
            <button class="settings-button settings-button-primary" type="button" data-settings-save>
              Save changes
            </button>
          </div>
        </footer>
      </aside>
    `;
  }

  bindEvents() {
    this.root.querySelectorAll("[data-settings-close]").forEach((button) => {
      button.addEventListener("click", () => this.close({ restore: true }));
    });

    this.root
      .querySelector("[data-settings-save]")
      .addEventListener("click", () => {
        this.onSave(cloneBrandConfig(this.draft));
        this.original = cloneBrandConfig(this.draft);
        this.close({ restore: false });
      });

    this.root
      .querySelector("[data-settings-reset]")
      .addEventListener("click", () => {
        this.draft = cloneBrandConfig(this.defaultConfig);
        this.renderSection();
        this.onReset(cloneBrandConfig(this.draft));
      });

    this.root.querySelectorAll("[data-settings-tab]").forEach((button) => {
      button.addEventListener("click", () => {
        this.activeSection = button.dataset.settingsTab;
        this.renderSection();
      });
    });

    this._onHashChange = () => this.syncHashState();
    this._onDocumentKeydown = (event) => {
      if (event.key === "Escape" && this.isOpen()) {
        this.close({ restore: true });
      }
    };
    window.addEventListener("hashchange", this._onHashChange);
    document.addEventListener("keydown", this._onDocumentKeydown);
  }

  unmount() {
    if (this._onHashChange) {
      window.removeEventListener("hashchange", this._onHashChange);
    }
    if (this._onDocumentKeydown) {
      document.removeEventListener("keydown", this._onDocumentKeydown);
    }
    this.trigger?.remove();
    this.root?.remove();
    document.body.classList.remove("settings-open");
    this.trigger = null;
    this.root = null;
    this.formContent = null;
    this.livePreviewInput = null;
  }

  syncHashState() {
    if (window.location.hash === "#settings") {
      this.open();
    }
  }

  open() {
    this.original = cloneBrandConfig(this.getBrandConfig());
    this.draft = cloneBrandConfig(this.original);
    this.root.hidden = false;
    this.root.classList.add("is-open");
    this.trigger.hidden = true;
    document.body.classList.add("settings-open");
    this.renderSection();
    this.root.querySelector("[data-settings-close]")?.focus();
  }

  close({ restore }) {
    if (restore) this.onPreview(cloneBrandConfig(this.original));
    this.root.classList.remove("is-open");
    this.root.hidden = true;
    this.trigger.hidden = false;
    document.body.classList.remove("settings-open");

    if (window.location.hash === "#settings") {
      window.history.pushState(
        "",
        document.title,
        window.location.pathname + window.location.search,
      );
    }
  }

  isOpen() {
    return this.root?.classList.contains("is-open");
  }

  renderSection() {
    this.root.querySelectorAll("[data-settings-tab]").forEach((button) => {
      button.setAttribute(
        "aria-selected",
        String(button.dataset.settingsTab === this.activeSection),
      );
    });

    if (this.activeSection === "layout") {
      this.renderLayoutSection();
      return;
    }

    if (this.activeSection === "story") {
      this.renderStorySection();
      return;
    }

    if (this.activeSection === "cards") {
      this.renderCardsSection();
      return;
    }

    const section = SETTINGS_SECTIONS.find(
      ({ id }) => id === this.activeSection,
    );
    if (!section?.fields) return;

    this.formContent.replaceChildren(this.createSectionHeader(section));

    const grid = document.createElement("div");
    grid.className = "settings-field-grid";

    section.fields.forEach(([path, label, type, options]) => {
      grid.appendChild(this.createField({ path, label, type, options }));
    });

    this.formContent.appendChild(grid);
  }

  renderLayoutSection() {
    const section = SETTINGS_SECTIONS.find(({ id }) => id === "layout");
    const wrapper = document.createElement("div");
    wrapper.appendChild(this.createSectionHeader(section));

    if (!this.draft.layout) {
      this.draft.layout = { heroOverlayStyle: "transparent" };
    }

    wrapper.appendChild(
      this.createSelectField({
        path: "layout.heroOverlayStyle",
        label: "Hero intro background (layer 1)",
        options: [
          ["transparent", "Transparent - text over the 3D scene"],
          ["glass", "Glass panel - frosted background behind intro copy"],
        ],
      }),
    );

    const note = document.createElement("p");
    note.className = "settings-story-cta-hint";
    note.textContent =
      "Story panels (layer 2) stack full width below the hero intro. Use the Story tab to edit standard panels and use-case scenarios.";
    wrapper.appendChild(note);
    this.formContent.replaceChildren(wrapper);
  }

  renderStorySection() {
    const wrapper = document.createElement("div");
    wrapper.appendChild(
      this.createSectionHeader({
        eyebrow: "Scroll narrative",
        label: "Story",
        description: "Edit the copy used by the scroll-driven panels.",
      }),
    );

    const hintField = this.createField({
      path: "story.scrollHint",
      label: "Scroll hint",
      type: "text",
    });
    wrapper.appendChild(hintField);

    const list = document.createElement("div");
    list.className = "settings-story-list";

    this.draft.story.sections.forEach((section, index) => {
      const card = document.createElement("article");
      card.className = "settings-story-card";
      const panelLabel =
        section.layout === "useCases"
          ? `Panel ${index + 1} - Use cases`
          : `Panel ${index + 1}`;
      card.innerHTML = `<h3>${panelLabel}</h3>`;
      card.append(
        this.createField({
          path: `story.sections.${index}.kicker`,
          label: "Kicker",
          type: "text",
        }),
        this.createField({
          path: `story.sections.${index}.title`,
          label: "Title",
          type: "textarea",
        }),
        this.createField({
          path: `story.sections.${index}.outcome`,
          label: "Outcome (short line)",
          type: "text",
        }),
        this.createField({
          path: `story.sections.${index}.body`,
          label: "Body",
          type: "textarea",
        }),
      );

      if (section.layout === "useCases") {
        this.appendUseCaseStoryFields(card, index, section);
      } else {
        card.append(
          this.createStoryBulletsField(
            `story.sections.${index}.bullets`,
            "Bullets (one per line)",
          ),
        );
      }

      card.append(
        this.createStoryAccentField(index),
        this.createField({
          path: `story.sections.${index}.ctaLabel`,
          label: "Primary CTA label",
          type: "text",
        }),
        this.createField({
          path: `story.sections.${index}.ctaPath`,
          label: "CTA path (internal, e.g. /contact)",
          type: "text",
        }),
        this.createField({
          path: `story.sections.${index}.ctaSlug`,
          label: "CTA service slug (e.g. receptionist)",
          type: "text",
        }),
        this.createField({
          path: `story.sections.${index}.ctaHref`,
          label: "CTA external URL (https or mailto only)",
          type: "text",
        }),
        this.createField({
          path: `story.sections.${index}.secondaryLabel`,
          label: "Secondary link label",
          type: "text",
        }),
        this.createField({
          path: `story.sections.${index}.secondaryHref`,
          label:
            "Secondary URL (path starting with /, or https/mailto)",
          type: "text",
        }),
      );
      const hint = document.createElement("p");
      hint.className = "settings-story-cta-hint";
      hint.textContent =
        section.layout === "useCases"
          ? "Use-case scenarios are illustrative workflows - not client testimonials. Edit each scenario’s problem, configuration, and outcome below."
          : "If path, slug, and external URL are all set, the primary button uses path first, then slug (/services/…), then the external URL.";
      card.appendChild(hint);
      list.appendChild(card);
    });

    wrapper.appendChild(list);
    this.formContent.replaceChildren(wrapper);
  }

  renderCardsSection() {
    const wrapper = document.createElement("div");
    wrapper.appendChild(
      this.createSectionHeader({
        eyebrow: "Hover moments",
        label: "Cards",
        description:
          "Edit the floating cards that reveal extra context on hover or keyboard focus.",
      }),
    );

    const list = document.createElement("div");
    list.className = "settings-story-list";

    this.draft.featureCards.forEach((card, index) => {
      const cardEditor = document.createElement("article");
      cardEditor.className = "settings-story-card";
      cardEditor.innerHTML = `<h3>Card ${index + 1}</h3>`;
      cardEditor.append(
        this.createField({
          path: `featureCards.${index}.kicker`,
          label: "Kicker",
          type: "text",
        }),
        this.createField({
          path: `featureCards.${index}.title`,
          label: "Title",
          type: "text",
        }),
        this.createField({
          path: `featureCards.${index}.stat`,
          label: "Stat",
          type: "text",
        }),
        this.createField({
          path: `featureCards.${index}.body`,
          label: "Reveal body",
          type: "textarea",
        }),
      );
      list.appendChild(cardEditor);
    });

    wrapper.appendChild(list);
    this.formContent.replaceChildren(wrapper);
  }

  createSectionHeader(section) {
    const header = document.createElement("div");
    header.className = "settings-section-header";
    header.innerHTML = `
      <p>${section.eyebrow}</p>
      <h3>${section.label}</h3>
      <span>${section.description}</span>
    `;
    return header;
  }

  createField({ path, label, type, options = {} }) {
    const fieldId = `settings-${path.replaceAll(".", "-")}`;
    const wrapper = document.createElement("label");
    wrapper.className = `settings-field settings-field-${type}`;
    wrapper.setAttribute("for", fieldId);

    const value = getPath(this.draft, path);
    const meta = document.createElement("span");
    meta.className = "settings-field-label";
    meta.textContent = label;

    const control =
      type === "textarea"
        ? document.createElement("textarea")
        : document.createElement("input");
    control.id = fieldId;
    control.name = path;
    control.dataset.settingsPath = path;
    control.dataset.settingsType = type;

    if (type !== "textarea") control.type = type;
    if (type === "range" || type === "number") {
      control.min = options.min;
      control.max = options.max;
      control.step = options.step;
    }

    control.value = value == null ? "" : String(value);
    control.addEventListener("input", (event) => {
      const nextValue = normalizeValue(event.target.value, type);
      setPath(this.draft, path, nextValue);
      if (this.livePreviewInput.checked)
        this.onPreview(cloneBrandConfig(this.draft));
      this.updateRangeValue(event.target);
    });

    wrapper.append(meta);

    if (type === "color") {
      const colorRow = document.createElement("span");
      colorRow.className = "settings-color-row";
      const textInput = document.createElement("input");
      textInput.type = "text";
      textInput.value = value;
      textInput.setAttribute("aria-label", `${label} hex value`);

      control.addEventListener("input", () => {
        textInput.value = control.value;
      });
      textInput.addEventListener("input", (event) => {
        control.value = event.target.value;
        setPath(this.draft, path, event.target.value);
        if (this.livePreviewInput.checked)
          this.onPreview(cloneBrandConfig(this.draft));
      });

      colorRow.append(control, textInput);
      wrapper.append(colorRow);
    } else {
      wrapper.append(control);
    }

    if (type === "range") {
      const valueLabel = document.createElement("small");
      valueLabel.dataset.rangeValue = path;
      valueLabel.textContent = value;
      wrapper.appendChild(valueLabel);
    }

    return wrapper;
  }

  createStoryBulletsField(path, label) {
    const fieldId = `settings-${path.replaceAll(".", "-")}`;
    const wrapper = document.createElement("label");
    wrapper.className = "settings-field settings-field-textarea";
    wrapper.setAttribute("for", fieldId);
    const meta = document.createElement("span");
    meta.className = "settings-field-label";
    meta.textContent = label;
    const control = document.createElement("textarea");
    control.id = fieldId;
    control.dataset.settingsPath = path;
    const raw = getPath(this.draft, path);
    control.value = Array.isArray(raw) ? raw.join("\n") : "";
    control.addEventListener("input", () => {
      const lines = control.value
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      setPath(this.draft, path, lines);
      if (this.livePreviewInput.checked)
        this.onPreview(cloneBrandConfig(this.draft));
    });
    wrapper.append(meta, control);
    return wrapper;
  }

  /**
   * @param {number} index
   */
  /**
   * @param {HTMLElement} card
   * @param {number} index
   * @param {Record<string, unknown>} section
   */
  appendUseCaseStoryFields(card, index, section) {
    if (!section.universal) {
      setPath(this.draft, `story.sections.${index}.universal`, {
        title: "",
        body: "",
      });
    }
    if (!Array.isArray(section.scenarios)) {
      setPath(this.draft, `story.sections.${index}.scenarios`, []);
    }

    const universalHeading = document.createElement("h4");
    universalHeading.className = "settings-subheading";
    universalHeading.textContent = "Universal footer";
    card.appendChild(universalHeading);

    card.append(
      this.createField({
        path: `story.sections.${index}.universal.title`,
        label: "Footer title",
        type: "text",
      }),
      this.createField({
        path: `story.sections.${index}.universal.body`,
        label: "Footer body",
        type: "textarea",
      }),
    );

    const scenarios = getPath(this.draft, `story.sections.${index}.scenarios`);
    if (!Array.isArray(scenarios)) return;

    scenarios.forEach((scenario, scenarioIndex) => {
      const scenarioCard = document.createElement("div");
      scenarioCard.className = "settings-scenario-block";
      const heading = document.createElement("h4");
      heading.className = "settings-subheading";
      heading.textContent = `Scenario ${scenarioIndex + 1}${
        scenario?.category ? ` - ${scenario.category}` : ""
      }`;
      scenarioCard.appendChild(heading);

      scenarioCard.append(
        this.createField({
          path: `story.sections.${index}.scenarios.${scenarioIndex}.category`,
          label: "Business category",
          type: "text",
        }),
        this.createField({
          path: `story.sections.${index}.scenarios.${scenarioIndex}.headline`,
          label: "Headline (pain)",
          type: "text",
        }),
        this.createField({
          path: `story.sections.${index}.scenarios.${scenarioIndex}.problem`,
          label: "Problem",
          type: "textarea",
        }),
        this.createField({
          path: `story.sections.${index}.scenarios.${scenarioIndex}.solution`,
          label: "Configured (solution)",
          type: "textarea",
        }),
        this.createField({
          path: `story.sections.${index}.scenarios.${scenarioIndex}.outcome`,
          label: "Outcome",
          type: "textarea",
        }),
        this.createField({
          path: `story.sections.${index}.scenarios.${scenarioIndex}.serviceLabel`,
          label: "Service chip label",
          type: "text",
        }),
        this.createField({
          path: `story.sections.${index}.scenarios.${scenarioIndex}.servicePath`,
          label: "Service path (e.g. /receptionist)",
          type: "text",
        }),
        this.createCheckboxField({
          path: `story.sections.${index}.scenarios.${scenarioIndex}.featured`,
          label: "Featured on desktop (wider card)",
        }),
      );
      card.appendChild(scenarioCard);
    });
  }

  createSelectField({ path, label, options }) {
    const fieldId = `settings-${path.replaceAll(".", "-")}`;
    const wrapper = document.createElement("label");
    wrapper.className = "settings-field settings-field-select";
    wrapper.setAttribute("for", fieldId);

    const meta = document.createElement("span");
    meta.className = "settings-field-label";
    meta.textContent = label;

    const sel = document.createElement("select");
    sel.id = fieldId;
    options.forEach(([value, lab]) => {
      const opt = document.createElement("option");
      opt.value = value;
      opt.textContent = lab;
      sel.appendChild(opt);
    });

    const current = getPath(this.draft, path);
    sel.value = current ?? options[0]?.[0] ?? "";
    sel.addEventListener("change", () => {
      setPath(this.draft, path, sel.value);
      if (this.livePreviewInput.checked)
        this.onPreview(cloneBrandConfig(this.draft));
    });

    wrapper.append(meta, sel);
    return wrapper;
  }

  createCheckboxField({ path, label }) {
    const fieldId = `settings-${path.replaceAll(".", "-")}`;
    const wrapper = document.createElement("label");
    wrapper.className = "settings-field settings-field-checkbox";

    const control = document.createElement("input");
    control.type = "checkbox";
    control.id = fieldId;
    control.checked = Boolean(getPath(this.draft, path));
    control.addEventListener("change", () => {
      setPath(this.draft, path, control.checked);
      if (this.livePreviewInput.checked)
        this.onPreview(cloneBrandConfig(this.draft));
    });

    const meta = document.createElement("span");
    meta.className = "settings-field-label";
    meta.textContent = label;

    wrapper.append(control, meta);
    return wrapper;
  }

  createStoryAccentField(index) {
    const path = `story.sections.${index}.accent`;
    const fieldId = `settings-${path.replaceAll(".", "-")}`;
    const wrapper = document.createElement("label");
    wrapper.className = "settings-field settings-field-select";
    wrapper.setAttribute("for", fieldId);
    const meta = document.createElement("span");
    meta.className = "settings-field-label";
    meta.textContent = "Panel accent";
    const sel = document.createElement("select");
    sel.id = fieldId;
    [
      ["", "(none)"],
      ["primary", "Primary"],
      ["secondary", "Secondary"],
      ["accent", "Accent"],
    ].forEach(([value, lab]) => {
      const opt = document.createElement("option");
      opt.value = value;
      opt.textContent = lab;
      sel.appendChild(opt);
    });
    const current = getPath(this.draft, path);
    sel.value =
      current === "primary" ||
      current === "secondary" ||
      current === "accent"
        ? current
        : "";
    sel.addEventListener("change", () => {
      const keys = path.split(".");
      const lastKey = keys.pop();
      const target = keys.reduce((current, key) => current[key], this.draft);
      const v = sel.value;
      if (v) target[lastKey] = v;
      else delete target[lastKey];
      if (this.livePreviewInput.checked)
        this.onPreview(cloneBrandConfig(this.draft));
    });
    wrapper.append(meta, sel);
    return wrapper;
  }

  updateRangeValue(input) {
    if (input.type !== "range") return;
    this.root.querySelector(`[data-range-value="${input.name}"]`).textContent =
      input.value;
  }
}

function getPath(source, path) {
  return path.split(".").reduce((value, key) => value?.[key], source);
}

function setPath(source, path, value) {
  const keys = path.split(".");
  const lastKey = keys.pop();
  const target = keys.reduce((current, key) => current[key], source);
  target[lastKey] = value;
}

function normalizeValue(value, type) {
  if (type === "number" || type === "range") return Number(value);
  return value;
}
